import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Layout } from '@/components/Layout';
import { Shield, Award, FileCheck, X, ZoomIn } from 'lucide-react';

const documents = [
  {
    id: 1,
    title: 'اعتماد مؤسسي',
    subtitle: 'كلية بوابة التكنولوجيا',
    issuer: 'الهيئة الليبية لضمان جودة التعليم',
    validUntil: '2026/08/28',
    type: 'اعتماد',
    icon: Award,
    color: 'from-blue-600 to-blue-800',
    badgeColor: 'bg-blue-100 text-blue-800',
    image: '/images/اعتمادة الجودة الكلية.jpg',
    description: 'اعتماد مؤسسي ممنوح لكلية بوابة التكنولوجيا في 6 تخصصات بموجب القرار رقم (72) لسنة 2024م',
  },
  {
    id: 2,
    title: 'اعتماد برامجي',
    subtitle: 'معهد بوابة التكنولوجيا',
    issuer: 'الهيئة الليبية لضمان جودة التعليم',
    validUntil: '2026/08/28',
    type: 'اعتماد',
    icon: Award,
    color: 'from-amber-500 to-amber-700',
    badgeColor: 'bg-amber-100 text-amber-800',
    image: '/images/اعتماد الجودة المعهد.jpg',
    description: 'اعتماد برامجي ممنوح لمعهد بوابة التكنولوجيا في 6 تخصصات بموجب القرار رقم (73) لسنة 2024م',
  },
  {
    id: 3,
    title: 'إذن مزاولة نشاط تعليمي',
    subtitle: 'كلية بوابة التكنولوجيا - رقم (08)',
    issuer: 'وزارة التعليم التقني والفني',
    validUntil: 'ربيع 2025م',
    type: 'ترخيص',
    icon: FileCheck,
    color: 'from-emerald-600 to-emerald-800',
    badgeColor: 'bg-emerald-100 text-emerald-800',
    image: '/images/اذن مزاولة الكلية.jpg',
    description: 'إذن مزاولة نشاط تعليمي للكليات التقنية الخاصة الصادر عن إدارة التعليم التقني والفني الخاص',
  },
  {
    id: 4,
    title: 'إذن مزاولة نشاط تعليمي',
    subtitle: 'معهد بوابة التكنولوجيا العالي - رقم (107)',
    issuer: 'وزارة التعليم التقني والفني',
    validUntil: 'ربيع 2025م',
    type: 'ترخيص',
    icon: FileCheck,
    color: 'from-purple-600 to-purple-800',
    badgeColor: 'bg-purple-100 text-purple-800',
    image: '/images/اذن مزاولة المعهد.jpg',
    description: 'إذن مزاولة نشاط تعليمي للمعاهد التقنية الخاصة الصادر عن إدارة التعليم التقني والفني الخاص',
  },
  {
    id: 5,
    title: 'إذن مزاولة نشاط تعليمي',
    subtitle: 'معهد بوابة التكنولوجيا المتوسط - رقم (188)',
    issuer: 'وزارة التعليم التقني والفني',
    validUntil: '9/9/2026م',
    type: 'ترخيص',
    icon: FileCheck,
    color: 'from-teal-600 to-teal-800',
    badgeColor: 'bg-teal-100 text-teal-800',
    image: '/images/اذن مزاولة المتوسط.jpg',
    description: 'إذن مزاولة نشاط تعليمي للمعاهد الفنية الخاصة الصادر عن إدارة التعليم التقني والفني الخاص - فرع بنغازي',
  },
  {
    id: 6,
    title: 'اعتماد مؤسسي',
    subtitle: 'معهد بوابة التكنولوجيا - بنغازي',
    issuer: 'المركز الوطني لضمان الجودة - المنطقة الشرقية',
    validUntil: '2026/12/27',
    type: 'اعتماد',
    icon: Award,
    color: 'from-rose-600 to-rose-800',
    badgeColor: 'bg-rose-100 text-rose-800',
    image: '/images/اعتمادات المعهد.pdf_13.jpg',
    description: 'اعتماد مؤسسي نهائي لمعهد بوابة التكنولوجيا - بنغازي في 6 تخصصات بموجب القرار رقم 37 لسنة 2020م، صالح حتى 2026/12/27',
  },
];

export default function Accreditations() {
  const [selectedDoc, setSelectedDoc] = useState<typeof documents[0] | null>(null);

  return (
    <Layout>
      <div className="min-h-screen bg-background" dir="rtl">
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-primary/10 via-background to-accent/5 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-10 right-10 w-64 h-64 rounded-full bg-primary blur-3xl" />
            <div className="absolute bottom-10 left-10 w-48 h-48 rounded-full bg-accent blur-3xl" />
          </div>
          <div className="container mx-auto px-4 text-center relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Shield className="w-4 h-4" />
                <span>وثائق رسمية معتمدة</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
                الاعتمادات والتراخيص
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                تفخر كلية بوابة التكنولوجيا بحصولها على اعتمادات وتراخيص رسمية من الجهات الحكومية الليبية المختصة، مما يضمن جودة التعليم والاعتراف الرسمي بالشهادات الممنوحة
              </p>
            </motion.div>

            {/* Stats */}
            <motion.div
              className="grid grid-cols-3 gap-6 max-w-lg mx-auto mt-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              {[
                { num: '3', label: 'اعتماد جودة' },
                { num: '3', label: 'إذن مزاولة' },
                { num: '2026', label: 'صالحة حتى' },
              ].map((s, i) => (
                <div key={i} className="bg-background/80 backdrop-blur rounded-xl p-4 border border-border shadow-sm">
                  <div className="text-2xl font-bold text-primary">{s.num}</div>
                  <div className="text-xs text-muted-foreground mt-1">{s.label}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Documents Grid */}
        <section className="py-16 container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {documents.map((doc, idx) => (
              <motion.div
                key={doc.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="group bg-card rounded-2xl border border-border overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer"
                onClick={() => setSelectedDoc(doc)}
              >
                {/* Document Preview */}
                <div className="relative overflow-hidden bg-muted" style={{ aspectRatio: '3/4' }}>
                  <img
                    src={doc.image}
                    alt={doc.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 right-3">
                    <span className={`text-xs font-medium px-3 py-1 rounded-full ${doc.badgeColor}`}>
                      {doc.type}
                    </span>
                  </div>
                  <div className="absolute bottom-3 left-3">
                    <div className="flex items-center gap-1 bg-black/50 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full">
                      <ZoomIn className="w-3 h-3" />
                      <span>اضغط للتكبير</span>
                    </div>
                  </div>
                </div>

                {/* Document Info */}
                <div className="p-5">
                  <div className={`inline-flex items-center gap-2 bg-gradient-to-r ${doc.color} text-white text-xs px-3 py-1 rounded-full mb-3`}>
                    <doc.icon className="w-3 h-3" />
                    <span>{doc.title}</span>
                  </div>
                  <h3 className="font-bold text-foreground text-base mb-1">{doc.subtitle}</h3>
                  <p className="text-sm text-muted-foreground mb-3 leading-relaxed">{doc.description}</p>
                  <div className="flex items-center justify-between text-xs text-muted-foreground border-t border-border pt-3">
                    <span className="flex items-center gap-1">
                      <Shield className="w-3 h-3 text-primary" />
                      {doc.issuer}
                    </span>
                    <span className="text-primary font-medium">حتى {doc.validUntil}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Lightbox Modal */}
        <AnimatePresence>
          {selectedDoc && (
            <motion.div
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedDoc(null)}
            >
              <motion.div
                className="relative max-w-2xl w-full bg-background rounded-2xl overflow-hidden shadow-2xl"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="absolute top-3 left-3 z-10 bg-background/90 rounded-full p-2 shadow hover:bg-muted transition-colors"
                  onClick={() => setSelectedDoc(null)}
                >
                  <X className="w-5 h-5" />
                </button>
                <img
                  src={selectedDoc.image}
                  alt={selectedDoc.title}
                  className="w-full h-auto max-h-[80vh] object-contain"
                />
                <div className="p-4 border-t border-border">
                  <h3 className="font-bold text-foreground">{selectedDoc.subtitle}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{selectedDoc.issuer} · صالحة حتى {selectedDoc.validUntil}</p>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Layout>
  );
}
